// ✅ src/components/screens/EditProductScreen.js
// Seller Edit Product (FULL SCREEN)
// ✅ Mobile-friendly selects: bottom-sheet picker on small screens (Category / Discount Type / Status)
// ✅ Currency locked to GHS (no dropdown) — REMOVED currency state to fix react-hooks/exhaustive-deps
// ✅ Fields (Extra Details) OLD layout: 3-column row (key/value/remove)
// ✅ UPDATED: Removed preset buttons (Fashion / Shoes / Phone) — only “+ Add field” remains

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db, storage } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function moneyToNumber(v) {
  const n = Number(String(v || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function numToInt(v) {
  const n = Number(String(v ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function uid2() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}
function normalizeAttributes(mapLike) {
  const out = {};
  const raw = mapLike || {};
  for (const k of Object.keys(raw)) {
    const key = safe(k);
    const val = safe(raw[k]);
    if (!key || !val) continue;

    const lower = key.toLowerCase();
    if (lower === "colour") out["Color"] = val;
    else if (lower === "color") out["Color"] = val;
    else if (lower === "size") out["Size"] = val;
    else out[key] = val;
  }
  return out;
}

/* ---------------- tiny hook: mobile breakpoint ---------------- */
function useIsMobile(breakpointPx = 520) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpointPx;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpointPx);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isMobile;
}

/* ---------------- MobileSelect (bottom sheet on mobile) ---------------- */
function MobileSelect({ label, value, onChange, options, disabled, isMobile }) {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    const hit = options.find((o) => safe(o.value) === safe(value));
    return safe(hit?.label) || "Select…";
  }, [options, value]);

  if (!isMobile) {
    return (
      <label style={styles.label}>
        {label}
        <select
          style={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {options.map((o) => (
            <option key={safe(o.value)} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <>
      <label style={styles.label}>
        {label}
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(true)}
          style={{
            ...styles.selectBtn,
            opacity: disabled ? 0.65 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {selectedLabel}
          </span>
          <span style={styles.chev}>▾</span>
        </button>
      </label>

      {open ? (
        <div style={styles.sheetOverlay} onClick={() => setOpen(false)}>
          <div
            style={styles.sheet}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div style={styles.sheetTop}>
              <div style={styles.sheetTitle}>{label}</div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={styles.sheetClose}
              >
                ✕
              </button>
            </div>

            <div style={styles.sheetList}>
              {options.map((o) => {
                const active = safe(o.value) === safe(value);
                return (
                  <button
                    key={safe(o.value)}
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    style={{
                      ...styles.sheetItem,
                      ...(active ? styles.sheetItemActive : null),
                    }}
                  >
                    <span>{o.label}</span>
                    {active ? <span style={styles.tick}>✓</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function EditProductScreen({ goBack, productId: productIdProp }) {
  const nav = useNavigate();
  const params = useParams();
  const productId = safe(productIdProp || params.productId);

  const isMobile = useIsMobile(520);
  const fileRef = useRef(null);

  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // categories
  const [catsLoading, setCatsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");

  // product
  const [product, setProduct] = useState(null);

  // form fields
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("1");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [productType, setProductType] = useState("");

  // discount
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountMode, setDiscountMode] = useState("PERCENT"); // PERCENT | FINAL_PRICE
  const [discountPercent, setDiscountPercent] = useState("0");
  const [discountFinalPrice, setDiscountFinalPrice] = useState("");

  // attributes
  const [attributes, setAttributes] = useState([{ key: "", value: "" }]);

  // images [{url, path?, name?}]
  const [images, setImages] = useState([]);
  const [thumbIndex, setThumbIndex] = useState(0);

  // upload state
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  /* ---------------- auth ---------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  /* ---------------- load store ---------------- */
  useEffect(() => {
    if (!user) return;
    let alive = true;

    (async () => {
      try {
        const snap = await getDoc(doc(db, "stores", user.uid));
        if (!alive) return;
        setStore(snap.exists() ? { id: user.uid, ...(snap.data() || {}) } : null);
      } catch {
        if (!alive) return;
        setStore(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  /* ---------------- load categories ---------------- */
  useEffect(() => {
    if (!user) return;
    let alive = true;

    (async () => {
      setCatsLoading(true);
      try {
        const qy = query(
          collection(db, "stores", user.uid, "categories"),
          orderBy("order", "asc")
        );
        const snap = await getDocs(qy);
        if (!alive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setCategories(list);
      } catch {
        if (!alive) return;
        setCategories([]);
      } finally {
        if (alive) setCatsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  /* ---------------- load product ---------------- */
  useEffect(() => {
    if (!user) return;
    if (!productId) {
      setMsg("Missing product id.");
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoading(true);
      setMsg("");

      try {
        const snap = await getDoc(doc(db, "products", productId));
        if (!snap.exists()) {
          if (!alive) return;
          setMsg("Product not found.");
          setLoading(false);
          return;
        }

        const data = { id: snap.id, ...(snap.data() || {}) };

        if (safe(data.storeId) !== safe(user.uid)) {
          if (!alive) return;
          setMsg("You can’t edit a product that isn’t yours.");
          setLoading(false);
          return;
        }

        if (!alive) return;
        setProduct(data);

        setTitle(safe(data.title));
        setPrice(String(data.price ?? ""));
        setStock(String(data.stock ?? "1"));
        setDescription(safe(data.description));

        setStatus(safe(data.status) || "ACTIVE");
        setProductType(safe(data.productType));

        const dp = Number(data.discountPercent || 0) || 0;
        setDiscountEnabled(dp > 0 || !!data.hasDiscount);
        setDiscountMode("PERCENT");
        setDiscountPercent(String(dp || 0));
        setDiscountFinalPrice("");

        setCategoryId(safe(data.categoryId));

        const attrs =
          data.attributes && typeof data.attributes === "object" ? data.attributes : {};
        const rows = Object.keys(attrs).map((k) => ({ key: k, value: safe(attrs[k]) }));
        setAttributes(rows.length ? rows : [{ key: "", value: "" }]);

        const urls = Array.isArray(data.mediaUrls)
          ? data.mediaUrls
          : safe(data.thumbnailUrl)
          ? [safe(data.thumbnailUrl)]
          : [];

        const imgs = urls
          .map((url) => ({ url: safe(url), path: "", name: "" }))
          .filter((x) => x.url);

        setImages(imgs);
        const ti = Number(data.thumbnailIndex ?? 0);
        setThumbIndex(
          Number.isFinite(ti) && ti >= 0
            ? Math.min(ti, Math.max(0, imgs.length - 1))
            : 0
        );

        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load product.");
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, productId]);

  const storeName = useMemo(
    () => safe(store?.storeName) || safe(user?.displayName) || "Your Store",
    [store, user]
  );

  const computedDiscountPercent = useMemo(() => {
    const p = moneyToNumber(price);
    if (!discountEnabled) return 0;

    if (discountMode === "PERCENT") {
      const dp = Math.max(0, Math.min(95, numToInt(discountPercent)));
      return dp;
    }

    const fp = moneyToNumber(discountFinalPrice);
    if (!p || p <= 0) return 0;
    if (!fp || fp <= 0) return 0;
    if (fp >= p) return 0;

    const pct = Math.round(((p - fp) / p) * 100);
    return Math.max(0, Math.min(95, pct));
  }, [price, discountEnabled, discountMode, discountPercent, discountFinalPrice]);

  const cleanedAttributes = useMemo(() => {
    const map = {};
    for (const row of attributes) {
      const k = safe(row.key);
      const v = safe(row.value);
      if (!k || !v) continue;
      map[k.slice(0, 40)] = v.slice(0, 200);
    }
    return map;
  }, [attributes]);

  const normalizedAttributes = useMemo(
    () => normalizeAttributes(cleanedAttributes),
    [cleanedAttributes]
  );

  /* ---------------- images ---------------- */
  const openPicker = () => {
    if (uploading) return;
    fileRef.current?.click();
  };

  const uploadFiles = async (files) => {
    if (!user) return;
    if (!files?.length) return;

    setUploading(true);
    setUploadPct(0);
    setMsg("");

    try {
      const uploaded = [];

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const ext = safe(f.name.split(".").pop()).toLowerCase() || "jpg";
        const path = `stores/${user.uid}/products/${productId}/${uid2()}.${ext}`;
        const r = ref(storage, path);

        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(r, f);

          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              const overall = Math.round(((i + pct / 100) / files.length) * 100);
              setUploadPct(overall);
            },
            (err) => reject(err),
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploaded.push({ url, path, name: f.name });
              resolve();
            }
          );
        });
      }

      setImages((prev) => {
        const next = [...prev, ...uploaded];
        if (next.length && prev.length === 0) setThumbIndex(0);
        return next;
      });

      setMsg("✅ Images uploaded.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Upload failed. Try again.");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(files);
  };

  const removeImage = async (idx) => {
    const img = images[idx];
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);

      setThumbIndex((old) => {
        if (!next.length) return 0;
        if (idx < old) return Math.max(0, old - 1);
        if (idx === old) return 0;
        return old;
      });

      return next;
    });

    const p = safe(img?.path);
    if (p) {
      try {
        await deleteObject(ref(storage, p));
      } catch {}
    }
  };

  /* ---------------- attributes ui ---------------- */
  const addAttrRow = () => setAttributes((prev) => [...prev, { key: "", value: "" }]);
  const removeAttrRow = (idx) => setAttributes((prev) => prev.filter((_, i) => i !== idx));
  const updateAttrRow = (idx, patch) =>
    setAttributes((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  /* ---------------- categories ---------------- */
  const createCategory = async () => {
    if (!user) return;
    const name = safe(newCategoryName);
    if (!name) return setMsg("Enter a category name.");

    setSaving(true);
    setMsg("");

    try {
      const id = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "")
        .slice(0, 40);

      const order =
        (categories?.[categories.length - 1]?.order ?? categories.length) + 1;

      await setDoc(doc(db, "stores", user.uid, "categories", id), {
        name,
        order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewCategoryName("");

      const qy = query(
        collection(db, "stores", user.uid, "categories"),
        orderBy("order", "asc")
      );
      const snap = await getDocs(qy);
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setCategories(list);
      setCategoryId(id);

      setMsg("✅ Category added.");
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to add category.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- save ---------------- */
  const onSave = async () => {
    if (!user) return setMsg("Please login first.");
    if (!product) return setMsg("Product missing.");
    if (safe(product.storeId) !== safe(user.uid)) return setMsg("Not allowed.");

    const t = safe(title);
    if (!t) return setMsg("Product title is required.");

    const p = moneyToNumber(price);
    const s = numToInt(stock);

    if (!Number.isFinite(p) || p <= 0) return setMsg("Enter a valid price.");
    if (!Number.isFinite(s) || s < 0) return setMsg("Enter a valid stock.");
    if (!images.length) return setMsg("Add at least 1 product image.");

    const thumbnailUrl = images[thumbIndex]?.url || images[0].url;

    const categoryName =
      safe(categories.find((c) => c.id === categoryId)?.name) ||
      safe(product?.categoryName) ||
      "";

    setSaving(true);
    setMsg("");

    try {
      await updateDoc(doc(db, "products", product.id), {
        storeId: user.uid,
        ownerId: user.uid,

        categoryId: safe(categoryId) || "",
        categoryName,

        title: t,
        shortDescription: safe(description).slice(0, 120),
        description: safe(description),

        price: p,
        currency: "GHS",

        discountPercent: discountEnabled ? computedDiscountPercent : 0,
        hasDiscount: !!discountEnabled && computedDiscountPercent > 0,

        stock: s,
        status: safe(status) || "ACTIVE",

        thumbnailUrl,
        mediaUrls: images.map((x) => x.url),
        mediaCount: images.length,
        thumbnailIndex: Math.max(0, Math.min(images.length - 1, thumbIndex)),

        productType: safe(productType),
        attributes: normalizedAttributes,

        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Product updated.");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setMsg(e?.message || "Failed to update product.");
    } finally {
      setSaving(false);
    }
  };

  const back = () => {
    if (typeof goBack === "function") return goBack();
    nav(-1);
  };

  // options for mobile selects
  const categoryOptions = useMemo(() => {
    if (!categories.length) {
      return [
        {
          value: "",
          label: catsLoading ? "Loading…" : "No categories yet (add one below)",
        },
      ];
    }
    return categories.map((c) => ({ value: c.id, label: safe(c.name) || c.id }));
  }, [categories, catsLoading]);

  const discountTypeOptions = useMemo(
    () => [
      { value: "PERCENT", label: "Percentage (%)" },
      { value: "FINAL_PRICE", label: "Final Price" },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: "ACTIVE", label: "ACTIVE (Visible)" },
      { value: "DRAFT", label: "DRAFT (Hidden)" },
    ],
    []
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.top}>
          <button onClick={back} style={styles.backBtn}>
            ‹ Back
          </button>
          <div>
            <div style={styles.title}>Edit Product</div>
            <div style={styles.sub}>
              Store: {storeName} {productId ? `• ID: ${productId}` : ""}
            </div>
          </div>
        </div>

        {loading ? <div style={styles.alert}>Loading…</div> : null}
        {msg ? <div style={styles.alert}>{msg}</div> : null}

        {!loading && product ? (
          <div style={styles.form}>
            {/* Images */}
            <div style={styles.uploadBox}>
              <div style={styles.uploadTop}>
                <div>
                  <div style={styles.boxTitle}>Product Images *</div>
                  <div style={styles.miniNote}>
                    Click + to add more photos. Tap an image to set thumbnail.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openPicker}
                  disabled={uploading}
                  style={{
                    ...styles.plusBtn,
                    opacity: uploading ? 0.7 : 1,
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                  title="Upload images"
                >
                  {uploading ? `${uploadPct}%` : "＋"}
                </button>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {images.length ? (
                <div style={styles.imageGrid}>
                  {images.map((img, idx) => (
                    <div key={img.url + idx} style={styles.imageCard}>
                      <img
                        src={img.url}
                        alt="product"
                        style={styles.img}
                        onClick={() => setThumbIndex(idx)}
                      />

                      <div style={styles.imageActions}>
                        <button
                          type="button"
                          onClick={() => setThumbIndex(idx)}
                          style={{
                            ...styles.pill,
                            background: idx === thumbIndex ? "#0f172a" : "#fff",
                            color: idx === thumbIndex ? "#fff" : "#0f172a",
                          }}
                        >
                          {idx === thumbIndex ? "Thumbnail" : "Set thumb"}
                        </button>

                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          style={styles.pillDanger}
                          title="Remove"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyUpload}>
                  No images yet. Click the <b>＋</b> to upload.
                </div>
              )}
            </div>

            {/* Category */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Category (seller-owned)</div>
              </div>

              <MobileSelect
                label="Choose category"
                value={categoryId}
                onChange={setCategoryId}
                options={categoryOptions}
                disabled={catsLoading}
                isMobile={isMobile}
              />

              <div style={styles.rowInline2}>
                <input
                  style={styles.input}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Create new category e.g. Dresses"
                />
                <button
                  type="button"
                  onClick={createCategory}
                  disabled={saving}
                  style={styles.smallBtn}
                >
                  + Add
                </button>
              </div>

              <div style={styles.miniNote}>
                Each seller has their own categories (clean + simple).
              </div>
            </div>

            {/* Title */}
            <label style={styles.label}>
              Product Title *
              <input
                style={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Blue Summer Dress"
              />
            </label>

            {/* Product Type */}
            <label style={styles.label}>
              Product Type (optional)
              <input
                style={styles.input}
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                placeholder="e.g. Dress / Slippers / iPhone 13"
              />
              <div style={styles.miniNote}>
                Not forced. Helps buyers understand instantly.
              </div>
            </label>

            {/* Price / Stock */}
            <div
              style={{
                ...styles.row2,
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              }}
            >
              <label style={styles.label}>
                Price *
                <div
                  style={{
                    ...styles.rowInline,
                    gridTemplateColumns: isMobile ? "1fr" : "110px 1fr",
                  }}
                >
                  <input style={styles.input} value="GHS" disabled />
                  <input
                    style={styles.input}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. 250"
                    inputMode="decimal"
                  />
                </div>
              </label>

              <label style={styles.label}>
                Stock *
                <input
                  style={styles.input}
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="e.g. 10"
                  inputMode="numeric"
                />
              </label>
            </div>

            {/* Discount */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Discount (optional)</div>

                <label style={styles.switchWrap}>
                  <input
                    type="checkbox"
                    checked={discountEnabled}
                    onChange={(e) => setDiscountEnabled(e.target.checked)}
                  />
                  <span style={styles.switchText}>Enable</span>
                </label>
              </div>

              {discountEnabled ? (
                <>
                  <div
                    style={{
                      ...styles.row2,
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    }}
                  >
                    <MobileSelect
                      label="Discount Type"
                      value={discountMode}
                      onChange={setDiscountMode}
                      options={discountTypeOptions}
                      disabled={false}
                      isMobile={isMobile}
                    />

                    {discountMode === "PERCENT" ? (
                      <label style={styles.label}>
                        Discount %
                        <input
                          style={styles.input}
                          value={discountPercent}
                          onChange={(e) => setDiscountPercent(e.target.value)}
                          placeholder="e.g. 10"
                          inputMode="numeric"
                        />
                      </label>
                    ) : (
                      <label style={styles.label}>
                        Discounted Price
                        <input
                          style={styles.input}
                          value={discountFinalPrice}
                          onChange={(e) => setDiscountFinalPrice(e.target.value)}
                          placeholder="e.g. 150"
                          inputMode="decimal"
                        />
                      </label>
                    )}
                  </div>

                  <div style={styles.miniNote}>
                    Saved as <b>discountPercent</b>: {computedDiscountPercent}%
                  </div>
                </>
              ) : (
                <div style={styles.miniNote}>Leave off if no discount.</div>
              )}
            </div>

            {/* Description */}
            <label style={styles.label}>
              Description (optional)
              <textarea
                style={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description…"
              />
            </label>

            {/* ✅ Extra fields (OLD layout) */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Extra Details (optional)</div>

                {/* ✅ Only Add field button */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={addAttrRow} style={styles.smallBtn}>
                    + Add field
                  </button>
                </div>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {attributes.map((row, idx) => (
                  <div key={idx} style={styles.attrRowOld}>
                    <input
                      style={styles.input}
                      value={row.key}
                      onChange={(e) => updateAttrRow(idx, { key: e.target.value })}
                      placeholder="Field e.g. Size"
                    />
                    <input
                      style={styles.input}
                      value={row.value}
                      onChange={(e) => updateAttrRow(idx, { value: e.target.value })}
                      placeholder="Value e.g. S, M, L"
                    />
                    {attributes.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeAttrRow(idx)}
                        style={styles.trashBtn}
                        title="Remove"
                      >
                        ✕
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>
                ))}
              </div>

              <div style={styles.miniNote}>
                Add anything: size, color, storage, condition, material…
              </div>
            </div>

            {/* Status */}
            <MobileSelect
              label="Status"
              value={status}
              onChange={setStatus}
              options={statusOptions}
              disabled={false}
              isMobile={isMobile}
            />

            <button
              onClick={onSave}
              disabled={saving || uploading}
              style={{
                ...styles.saveBtn,
                opacity: saving || uploading ? 0.7 : 1,
                cursor: saving || uploading ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : uploading ? "Uploading images…" : "✅ Update Product"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f3f6ff",
    padding: 16,
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    color: "#0f172a",
  },
  card: {
    maxWidth: 760,
    margin: "0 auto",
    background: "#fff",
    border: "1px solid #e7ecf6",
    borderRadius: 18,
    boxShadow: "0 16px 45px rgba(15,23,42,0.08)",
    padding: 14,
  },
  top: { display: "flex", gap: 12, alignItems: "center" },
  backBtn: {
    border: "1px solid #e7ecf6",
    background: "#f7fbff",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  title: { fontSize: 18, fontWeight: 1000 },
  sub: { marginTop: 2, color: "#64748b", fontWeight: 900, fontSize: 12 },
  alert: {
    marginTop: 10,
    background: "#fbfdff",
    border: "1px solid #eef2ff",
    borderRadius: 12,
    padding: 10,
    fontWeight: 900,
  },
  form: { marginTop: 12, display: "grid", gap: 12 },
  label: { display: "grid", gap: 6, fontWeight: 950, fontSize: 13 },

  input: {
    border: "1px solid #e7ecf6",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    width: "100%",
    background: "#fff",
  },

  textarea: {
    border: "1px solid #e7ecf6",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    minHeight: 90,
    resize: "vertical",
    width: "100%",
  },

  row2: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" },
  rowInline: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 10 },
  rowInline2: { display: "grid", gridTemplateColumns: "1fr 110px", gap: 10 },

  saveBtn: {
    border: 0,
    background: "#0f172a",
    color: "#fff",
    borderRadius: 14,
    padding: "12px 12px",
    fontWeight: 1000,
  },

  box: {
    border: "1px solid #eef2ff",
    background: "#fbfdff",
    borderRadius: 14,
    padding: 12,
    display: "grid",
    gap: 10,
  },
  boxTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  boxTitle: { fontWeight: 1000, fontSize: 13 },
  miniNote: { fontSize: 12, color: "#64748b", fontWeight: 800 },

  switchWrap: { display: "flex", alignItems: "center", gap: 8, fontWeight: 900 },
  switchText: { fontSize: 12, color: "#0f172a" },

  smallBtn: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  // ✅ OLD attributes row (always 3 columns)
  attrRowOld: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 40px",
    gap: 10,
    alignItems: "center",
  },
  trashBtn: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 10,
    height: 42,
    fontWeight: 1000,
    cursor: "pointer",
  },

  uploadBox: {
    border: "1px dashed #cfd7ee",
    background: "#fbfdff",
    borderRadius: 14,
    padding: 12,
    display: "grid",
    gap: 12,
  },
  uploadTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  plusBtn: {
    width: 52,
    height: 52,
    borderRadius: 14,
    border: "1px solid #e7ecf6",
    background: "#0f172a",
    color: "#fff",
    fontSize: 28,
    fontWeight: 1000,
    lineHeight: "52px",
  },
  emptyUpload: {
    border: "1px solid #eef2ff",
    background: "#fff",
    borderRadius: 12,
    padding: 12,
    fontWeight: 900,
    color: "#64748b",
  },
  imageGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  },
  imageCard: {
    border: "1px solid #e7ecf6",
    borderRadius: 14,
    background: "#fff",
    overflow: "hidden",
    display: "grid",
    gap: 8,
    paddingBottom: 10,
  },
  img: {
    width: "100%",
    height: 140,
    objectFit: "cover",
    cursor: "pointer",
    display: "block",
  },
  imageActions: {
    display: "flex",
    gap: 8,
    padding: "0 10px",
    flexWrap: "wrap",
  },
  pill: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 999,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
  },
  pillDanger: {
    border: "1px solid #ffe0e0",
    background: "#fff5f5",
    borderRadius: 999,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
  },

  /* --- mobile select button --- */
  selectBtn: {
    border: "1px solid #e7ecf6",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    width: "100%",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    textAlign: "left",
  },
  chev: { opacity: 0.7, fontSize: 14 },

  /* --- bottom sheet --- */
  sheetOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    padding: 12,
    zIndex: 9999,
  },
  sheet: {
    width: "100%",
    maxWidth: 520,
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #e7ecf6",
    boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
    overflow: "hidden",
  },
  sheetTop: {
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #eef2ff",
  },
  sheetTitle: { fontWeight: 1000 },
  sheetClose: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 1000,
    cursor: "pointer",
  },
  sheetList: {
    display: "grid",
    padding: 8,
    gap: 8,
    maxHeight: "55vh",
    overflow: "auto",
  },
  sheetItem: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 12,
    padding: "12px 12px",
    fontWeight: 950,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    textAlign: "left",
  },
  sheetItemActive: { border: "1px solid #0f172a" },
  tick: { fontWeight: 1100 },
};
